import { reactive, watch, onScopeDispose, nextTick } from 'vue'
import { messageApi } from '@/api/messages'
import { startMessageStream } from '@/utils/messageStream'

// 所有响应带身份代次和请求序号；取消请求之外仍防止已完成的旧响应串入新身份。
export function useMessageNotifications(scope, api = messageApi) {
  const state = reactive({
    open: false, category: '', pendingOnly: false, unreadOnly: false, items: [], cursor: null, hasMore: false,
    loading: false, failedLoad: '', listError: '', summaryError: '', unreadCount: null, pendingCount: null,
    throughMessageId: 0, listedThroughId: 0, newMessages: false, scrollTop: 0,
    detail: null, detailId: null, detailLoading: false, detailError: '', readError: '', readLoading: false,
    adjacent: { previousId: null, nextId: null }, detailFilter: {}, away: false,
    originRoute: '', targetError: '', actionLoading: false, viewRevision: 0,
    detailUpdateAvailable: false, realtimeStatus: 'disabled',
  })
  let generation = 0, listRequest = 0, headRequest = 0, detailRequest = 0, summaryRequest = 0, summaryMutation = 0, refreshRequest = 0
  let abort = new AbortController(), timer, stream, failures = 0, listFailures = 0, stateFailures = 0, loadedStateError = '', detailSnapshot,
    summaryPromise, syncPromise, syncEpoch = 0, dirty = 0, mutationQueue = Promise.resolve()
  const canQuery = () => Boolean(scope.value) && (!api.isCurrentScope || api.isCurrentScope(scope.value))
  const mutate = task => { const result = mutationQueue.then(task, task); mutationQueue = result.catch(() => {}); return result }
  const params = () => ({ category: state.category || undefined, pendingOnly: state.pendingOnly, unreadOnly: state.unreadOnly })
  const matchesFilters = item => (!state.pendingOnly || item.processingStatus === 'PENDING') && (!state.unreadOnly || !item.readAt)
  // 未读列表会移除已读项，保留回执以免较早发出的列表响应把它重新插回。
  const readReceipts = new Map()
  let readThroughId = 0, readThroughAt = null
  const errorText = e => e.response?.data?.message || e.message || '请求失败，请重试'
  const applySummary = value => {
    const previousUnread = state.unreadCount, previousPending = state.pendingCount
    state.unreadCount = value.unreadCount
    state.pendingCount = value.pendingCount
    state.throughMessageId = value.throughMessageId
    state.summaryError = ''
    if (value.throughMessageId > state.listedThroughId || (previousUnread !== null && value.unreadCount > previousUnread) || (previousPending !== null && value.pendingCount > previousPending)) state.newMessages = true
  }
  // 已读时间只前进；处理状态按单条源版本比较，允许更高版本合法重新开启待办。
  const merge = (current, incoming) => {
    const readAt = incoming.readAt || current?.readAt || readReceipts.get(incoming.id) || (incoming.id <= readThroughId ? readThroughAt : null)
    if (readAt) readReceipts.set(incoming.id, readAt)
    return { ...(Number(current?.sourceRevision || 0) > Number(incoming.sourceRevision || 0) ? current : incoming), readAt }
  }
  async function summary() {
    if (!canQuery()) return
    if (summaryPromise) return summaryPromise
    const g = generation, request = ++summaryRequest, mutation = summaryMutation
    const pending = (async () => {
      try {
        const result = await api.summary(abort.signal)
        if (g !== generation || request !== summaryRequest || mutation !== summaryMutation) return
        applySummary(result); failures = 0
      } catch (e) {
        if (g === generation) { state.summaryError = errorText(e); failures++ }
      }
    })()
    summaryPromise = pending
    await pending
    if (summaryPromise === pending) summaryPromise = null
  }
  async function freshSummary() {
    const g = generation
    if (summaryPromise) await summaryPromise
    if (g === generation) await summary()
  }
  async function load(more = false) {
    if (!canQuery() || (more && (state.loading || !state.hasMore))) return
    const g = generation, request = ++listRequest, filter = params(), cursor = more ? state.cursor : undefined
    state.loading = true; state.listError = ''; state.failedLoad = ''
    try {
      const result = await api.list({ ...filter, cursor, pageSize: 20 }, abort.signal)
      if (g !== generation || request !== listRequest) return
      const merged = new Map((more ? state.items : []).map(item => [item.id, item]))
      const current = new Map(state.items.map(item => [item.id, item]))
      result.items.forEach(item => merged.set(item.id, merge(current.get(item.id), item)))
      state.items = [...merged.values()].filter(matchesFilters); state.cursor = result.nextCursor; state.hasMore = result.hasMore
      listFailures = 0
      if (!more) { state.listedThroughId = result.throughMessageId; state.newMessages = false; state.scrollTop = 0; loadedStateError = ''; stateFailures = 0 }
    } catch (e) {
      if (g === generation && request === listRequest) { state.listError = errorText(e); state.failedLoad = more ? 'more' : 'initial'; listFailures++ }
    } finally {
      if (g === generation && request === listRequest) state.loading = false
    }
  }
  async function refreshLatest(force = false) {
    if (!canQuery()) return
    const g = generation, view = state.viewRevision, request = ++headRequest, listVersion = listRequest, filter = params()
    try {
      const result = await api.list({ ...filter, pageSize: 20 }, abort.signal)
      if (g !== generation || request !== headRequest || listVersion !== listRequest || !state.open || state.away) return
      const current = new Map(state.items.map(item => [item.id, item]))
      const incoming = result.items.map(item => merge(current.get(item.id), item)).filter(matchesFilters)
      const unseen = incoming.some(item => !current.has(item.id))
      const reading = state.scrollTop > 8 || state.detailId || state.away
      if (!state.items.length || !reading || (force && unseen && view === state.viewRevision)) {
        // 使用新第一页及其游标作为完整连续窗口；不把不相邻的新旧页拼接成有缺口的分页。
        state.items = incoming
        state.cursor = result.nextCursor; state.hasMore = result.hasMore
        state.listedThroughId = result.throughMessageId; state.newMessages = false; state.scrollTop = 0
        loadedStateError = ''; stateFailures = 0
      } else if (unseen) state.newMessages = true
      state.listError = loadedStateError; state.failedLoad = loadedStateError ? 'refresh' : ''; listFailures = 0
    } catch (e) {
      if (g === generation && request === headRequest && listVersion === listRequest) {
        state.listError = errorText(e); state.failedLoad = 'refresh'; listFailures++
      }
    }
  }
  // 详情成功展示后自动、幂等地写已读：只写 read_at，不改 processing_status（待办仍需用户自己去处理）。
  // 与 markAllRead 共用 mutate 队列和 summaryMutation 序号，避免并发时旧 summary 覆盖新计数。
  async function markRead(id = state.detail?.id, manual = false) {
    if (!canQuery() || !id) return false
    const g = generation, request = detailRequest
    const currentDetail = () => g === generation && request === detailRequest && state.detail?.id === id
    // 详情与列表任意一处记着已读就不再提交；顺手把本地详情对齐，避免重开时短暂显示成未读。
    const settled = () => {
      const known = (state.detail?.id === id ? state.detail.readAt : null) || state.items.find(item => item.id === id)?.readAt
      if (known && state.detail?.id === id) state.detail.readAt = state.detail.readAt || known
      return known || null
    }
    if (settled()) return true
    if (manual) { state.readLoading = true; state.readError = '' }
    return mutate(async () => {
      try {
        if (g !== generation) return false
        // “全部已读”或上一次自动已读可能已经写过，不必重复提交。
        if (settled()) return true
        const mutation = ++summaryMutation
        const result = await api.read(id, abort.signal)
        if (g !== generation) return false
        summaryRequest++; summaryPromise = null
        if (mutation === summaryMutation) applySummary(result)
        const readAt = new Date().toISOString()
        readReceipts.set(id, readAt)
        state.items = state.items.map(item => item.id === id ? { ...item, readAt: item.readAt || readAt } : item).filter(matchesFilters)
        if (currentDetail()) { state.detail.readAt = state.detail.readAt || readAt; state.readError = '' }
        return true
      } catch (e) {
        if (currentDetail()) state.readError = errorText(e)
        return false
      } finally { if (manual) state.readLoading = false }
    })
  }
  async function openDetail(id, keepFilter = false) {
    if (!canQuery()) return
    state.viewRevision++
    const g = generation, request = ++detailRequest
    state.detailId = id; state.detail = null; state.detailLoading = true; state.detailUpdateAvailable = false
    detailSnapshot = state.items.find(item => item.id === id)
    state.detailError = ''; state.targetError = ''; state.readError = ''; state.readLoading = false; state.actionLoading = false
    if (!keepFilter) state.detailFilter = params()
    state.adjacent = { previousId: null, nextId: null }
    let shown = false
    try {
      const detail = await api.detail(id, abort.signal)
      if (g !== generation || request !== detailRequest) return
      state.detail = merge(detailSnapshot, detail)
      shown = true
      const adjacent = await api.adjacent(id, state.detailFilter, abort.signal)
      if (g === generation && request === detailRequest) state.adjacent = adjacent
    } catch (e) { if (g === generation && request === detailRequest) state.detailError = errorText(e) }
    finally { if (g === generation && request === detailRequest) state.detailLoading = false }
    // 只在这条详情确实展示出来之后才回报已读：晚到的响应、被替换的详情、加载失败都不写。
    if (!shown || g !== generation || request !== detailRequest || state.detail?.id !== id) return
    await nextTick()
    if (g !== generation || request !== detailRequest || state.detail?.id !== id) return
    await markRead(id).catch(error => { if (state.detail?.id === id) state.readError = errorText(error) })
  }
  function closeDetail() {
    state.viewRevision++; detailRequest++
    Object.assign(state, { detailId: null, detail: null, detailLoading: false, detailError: '', readError: '', readLoading: false,
      actionLoading: false, targetError: '', adjacent: { previousId: null, nextId: null } })
    state.detailUpdateAvailable = false
    detailSnapshot = null
  }
  async function markAllRead() {
    if (!canQuery()) return false
    const g = generation, throughId = state.throughMessageId, mutation = ++summaryMutation
    return mutate(async () => {
      if (g !== generation) return false
      try {
        const result = await api.markAllRead(throughId, abort.signal)
        if (g !== generation) return false
        summaryRequest++; summaryPromise = null
        if (mutation === summaryMutation) applySummary(result)
        const readAt = new Date().toISOString()
        if (throughId > readThroughId) { readThroughId = throughId; readThroughAt = readAt }
        state.items = state.items.map(item => merge(item, item.id <= throughId ? { ...item, readAt: item.readAt || readAt } : item)).filter(matchesFilters)
        if (state.detail && state.detail.id <= throughId) { state.detail.readAt = state.detail.readAt || readAt; state.readError = '' }
        return true
      } catch (e) { if (g === generation) state.summaryError = errorText(e); return false }
    })
  }
  async function refreshLoaded(withSummary = true) {
    if (!canQuery()) return
    const g = generation, request = ++refreshRequest, listVersion = listRequest, items = [...state.items]
    const detailVersion = detailRequest
    const ids = [...new Set([...items.map(item => item.id), ...(state.detailId ? [state.detailId] : [])])]
    // 保留历史页和位置，只校准已展示项；不从第一页覆盖已加载分页。
    const results = api.states ? await Promise.allSettled(Array.from({ length: Math.ceil(ids.length / 50) }, (_, i) =>
      api.states(ids.slice(i * 50, (i + 1) * 50), abort.signal)))
      : await Promise.allSettled(ids.map(id => api.detail(id, abort.signal)))
    if (g !== generation || request !== refreshRequest || listVersion !== listRequest) return
    loadedStateError = results.some(result => result.status === 'rejected') ? '部分消息状态暂未刷新，请重试。' : ''
    state.listError = loadedStateError
    state.failedLoad = state.listError ? 'refresh' : ''
    stateFailures = loadedStateError ? stateFailures + 1 : 0
    const updated = new Map(results.flatMap(result => result.status === 'fulfilled' ? (Array.isArray(result.value) ? result.value : [result.value]) : []).map(item => [item.id, item]))
    const current = new Map(state.items.map(item => [item.id, item]))
    if (state.detailId && detailVersion === detailRequest && updated.has(state.detailId)) detailSnapshot = merge(detailSnapshot, updated.get(state.detailId))
    state.items = items.flatMap(item => {
      if (api.states && results.every(result => result.status === 'fulfilled') && !updated.has(item.id)) return []
      const value = merge(current.get(item.id), updated.get(item.id) || item)
      if (!matchesFilters(value)) return []
      return [value]
    })
    const detail = state.detail && updated.get(state.detail.id)
    if (detail && detailVersion === detailRequest) {
      const value = merge(state.detail, detail)
      if (Number(value.sourceRevision || 0) > Number(state.detail.sourceRevision || 0)
        && (value.title !== state.detail.title || value.summary !== state.detail.summary || JSON.stringify(value.content) !== JSON.stringify(state.detail.content))) state.detailUpdateAvailable = true
      Object.assign(state.detail, { processingStatus: value.processingStatus, readAt: value.readAt,
        sourceRevision: value.sourceRevision, actionLabel: value.actionLabel })
      // 补齐来源名称无需替换正在阅读的正文或重置滚动位置。
      if (value.content?.context) state.detail.content = { ...state.detail.content, context: value.content.context }
    }
    if (withSummary) await summary()
  }
  async function restoreReturnState(saved) {
    if (!canQuery()) return
    const g = generation, view = state.viewRevision
    // 列表只持久化 ID 和视图位置，刷新后逐批按当前身份读取真实状态。
    state.open = false
    state.category = saved.category; state.pendingOnly = saved.pendingOnly; state.unreadOnly = saved.unreadOnly === true
    await nextTick()
    if (g !== generation || view !== state.viewRevision) return
    const request = ++listRequest
    const ids = saved.loadedIds
    state.loading = true
    try {
      const batches = await Promise.all(Array.from({ length: Math.ceil(ids.length / 50) }, (_, i) => api.states(ids.slice(i * 50, (i + 1) * 50), abort.signal)))
      if (g !== generation || view !== state.viewRevision || request !== listRequest) return
      const values = new Map(batches.flat().map(item => [item.id, item]))
      state.items = ids.flatMap(id => {
        const value = values.get(id), item = value && merge(undefined, value)
        return item && (!state.category || item.category === state.category) && matchesFilters(item) ? [item] : []
      })
      Object.assign(state, { cursor: saved.cursor, hasMore: saved.hasMore, scrollTop: saved.scrollTop,
        listedThroughId: saved.listedThroughId, detailFilter: saved.detailFilter, listError: '', failedLoad: '' })
    } catch (e) {
      if (g === generation && view === state.viewRevision && request === listRequest) { state.listError = errorText(e); state.failedLoad = 'initial' }
    } finally { if (g === generation && view === state.viewRevision && request === listRequest) state.loading = false }
  }
  function retryList() { return state.failedLoad === 'refresh' ? refresh() : load(state.failedLoad === 'more') }
  async function open() {
    if (!canQuery()) return
    // 侧栏入口总是打开列表；旧详情、返回恢复和定位回包不能覆盖这次操作。
    closeDetail(); state.viewRevision++; listRequest++; state.loading = false
    syncEpoch++; syncPromise = null; headRequest++; refreshRequest++
    state.away = false; state.open = true; state.originRoute = ''
    const g = generation, view = state.viewRevision
    await Promise.all([freshSummary(), state.items.length ? refreshLoaded(false) : Promise.resolve()])
    if (g !== generation || view !== state.viewRevision || !state.open || state.away) return
    await refreshLatest(true)
  }
  function schedule() {
    clearTimeout(timer)
    if (!scope.value) return
    timer = setTimeout(async () => {
      if (!document.hidden) await refresh()
      schedule()
    }, Math.min(300000, (state.open && !state.away ? 15000 : 60000) * 2 ** Math.min(Math.max(failures, listFailures, stateFailures), 3)))
  }
  function visible() { if (!document.hidden && scope.value) { void refresh(); schedule() } }
  function refresh() {
    if (!canQuery()) return Promise.resolve()
    dirty++
    if (syncPromise) return syncPromise
    const g = generation, epoch = syncEpoch
    const pending = (async () => {
      let seen
      do {
        seen = dirty
        await freshSummary()
        if (g !== generation || epoch !== syncEpoch) return
        if (state.open && !state.away) {
          await refreshLoaded(false)
          if (g !== generation || epoch !== syncEpoch || !state.open || state.away) return
          await refreshLatest()
        }
      } while (g === generation && epoch === syncEpoch && seen !== dirty)
    })()
    syncPromise = pending
    void pending.finally(() => { if (syncPromise === pending) syncPromise = null })
    return pending
  }
  watch(() => [state.category, state.pendingOnly, state.unreadOnly], () => {
    syncEpoch++; syncPromise = null; headRequest++; refreshRequest++
    state.items = []; state.cursor = null; state.hasMore = false; state.scrollTop = 0
    if (state.open) void load()
  })
  watch(() => [state.open, state.away], schedule)
  watch(scope, () => {
    stream?.stop(); stream = null
    readReceipts.clear(); readThroughId = 0; readThroughAt = null
    generation++; state.viewRevision++; abort.abort(); abort = new AbortController(); listRequest++; headRequest++; detailRequest++; summaryRequest++; summaryMutation++; refreshRequest++
    summaryPromise = null; syncPromise = null; syncEpoch++; dirty = 0; mutationQueue = Promise.resolve(); failures = 0; listFailures = 0; stateFailures = 0; loadedStateError = ''; detailSnapshot = null
    Object.assign(state, { open: false, items: [], cursor: null, hasMore: false, detail: null, detailId: null,
      detailLoading: false, detailError: '', listError: '', summaryError: '', unreadCount: null, pendingCount: null,
      throughMessageId: 0, listedThroughId: 0, newMessages: false, away: false, scrollTop: 0, loading: false, failedLoad: '', originRoute: '', targetError: '', actionLoading: false,
      readError: '', readLoading: false, adjacent: { previousId: null, nextId: null }, detailFilter: {}, detailUpdateAvailable: false, realtimeStatus: 'disabled' })
    void summary(); schedule()
    if (scope.value) {
      const g = generation
      stream = startMessageStream(api, {
        isCurrent: () => g === generation && canQuery(),
        onChange: () => { void refresh() },
        onStatus: value => { if (g === generation) state.realtimeStatus = value },
      })
    }
  }, { immediate: true, flush: 'sync' })
  document.addEventListener('visibilitychange', visible)
  window.addEventListener('biointelos-messages-updated', refresh)
  window.addEventListener('billing-gift-reminders-updated', refresh)
  window.addEventListener('online', visible)
  onScopeDispose(() => {
    generation++; stream?.stop(); abort.abort(); clearTimeout(timer)
    document.removeEventListener('visibilitychange', visible)
    window.removeEventListener('biointelos-messages-updated', refresh)
    window.removeEventListener('billing-gift-reminders-updated', refresh)
    window.removeEventListener('online', visible)
  })
  return { state, open, load, summary, openDetail, closeDetail, markRead, markAllRead, refreshLoaded, restoreReturnState, retryList, refresh }
}
